import { createSlice } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";
import type { SectionStatus } from "App/common/supplierSections";

/**
 * The supplier's own Company Profile.
 *
 * Distinct from `user.agency` / `useAgency`, which carry the *tenant-agency* id
 * off the customer JWT. A supplier's record lives in a_suppliers and is only
 * reachable through /suppliers/me*, so this slice owns it.
 */

export interface SectionDocument {
  id: number;
  section_key: string;
  field_name: string;
  filename: string;
  url: string;
  mimetype: string | null;
  size_bytes: number | null;
  uploaded_at: string;
}

export interface SectionRow {
  section_key: string;
  status: SectionStatus;
  data: Record<string, any> | null;
  documents: SectionDocument[];
  updated_at: string | null;
  /** Rules spanning sections that keep this one incomplete (server-computed),
   *  e.g. "registration number required for a limited company". */
  issues?: string[];
}

/** The registered company details — the single copy, on the supplier record. */
export interface RegisteredDetails {
  company_name: string;
  trading_name: string | null;
  registration_no: string | null;
  vat_number: string | null;
  address_1: string;
  address_2: string | null;
  town: string;
  county: string | null;
  postcode: string;
  country: string | null;
  telephone: string | null;
  email: string | null;
  website: string | null;
}

export interface Declaration {
  name: string;
  position: string;
  confirmed: boolean;
  /** Accepted the Supplier Terms and Conditions (c_pages 'supplier-terms-conditions'). */
  terms_accepted: boolean;
}

/** Company Profile approval. Only 'approved' lets the supplier operate. */
export type ProfileStatus = "draft" | "submitted" | "approved" | "rejected";

export interface ReviewNote {
  level: "info" | "warning" | "rejected" | "cleared";
  msg?: string;
}

/** One plan period (login service summarise()). */
export interface PlanSubscription {
  id: number;
  plan_id: number;
  plan_name: string | null;
  billing_period: "monthly" | "yearly" | "one_time" | null;
  status: "pending_payment" | "scheduled" | "active" | "expired" | "cancelled";
  starts_at: string | null;
  ends_at: string | null;
  price: number;
  /** VAT on top of price, and what the supplier pays (price + VAT). */
  tax_amount?: number;
  total?: number;
  currency: string;
  is_trial: boolean;
  payment_method: string | null;
  paid_at: string | null;
}

/** Admin-set ride terms — the supplier can read these, never change them. */
export interface SupplierSettings {
  own_rides_dispatch: "own_only" | "own_first" | "open";
  own_first_timeout_sec: number;
  accept_platform_rides: boolean;
  internal_ride_type: "percent" | "fixed";
  internal_ride_value: number;
  platform_ride_type: "percent" | "fixed";
  platform_ride_value: number;
  shared_ride_type: "percent" | "fixed";
  shared_ride_value: number;
  commission_base: "fare" | "total";
  driver_earnings_to: "driver" | "supplier";
}

/** SUPPLIER_PLANS_PLAN.md — what the supplier's plan allows right now. */
export interface PlanState {
  /** Plans required to operate (SUPPLIER_PLANS_ENFORCED on the server). */
  enforced: boolean;
  can_operate: boolean;
  reason: "PLAN_REQUIRED" | "PLAN_EXPIRED" | "PLAN_UNAVAILABLE" | null;
  in_grace: boolean;
  grace_until: string | null;
  subscription: PlanSubscription | null;
  pending: PlanSubscription | null;
  scheduled: PlanSubscription | null;
  /** Feature switches (boolean) and limits (number; null = unlimited). */
  features: Record<string, boolean | number | null>;
  settings: SupplierSettings;
  /** Dispatch modes / supplier fees in effect yet (later phase). */
  rules_active: boolean;
}

export interface SupplierSummary {
  id: number;
  /** Plan, features and ride terms. */
  plan_state?: PlanState;
  company_name: string;
  /** Account approval — lets the supplier sign in. */
  status: string;
  profile_status?: ProfileStatus;
  profile_submitted_at?: string | null;
  profile_reviewed_at?: string | null;
  /** Admin's overall note when sending the profile back. */
  profile_review_note?: string | null;
  /** Admin's per-section notes, keyed by section_key. */
  profile_review_messages?: Record<string, ReviewNote> | null;
  profile_declaration?: { name: string; position: string; confirmed_at: string } | null;
  registered?: RegisteredDetails;
}

/** The JSON column may arrive as a string; normalise once here. */
const normaliseSupplier = (raw: any): SupplierSummary | null => {
  if (!raw) return null;
  let messages = raw.profile_review_messages;
  if (typeof messages === "string") {
    try {
      messages = JSON.parse(messages);
    } catch {
      messages = null;
    }
  }
  return { ...raw, profile_review_messages: messages || {} };
};

/**
 * Company Profile and fleet responses carry the supplier without plan_state
 * (only /suppliers/me has it); keep the one already loaded.
 */
const withPlanState = (raw: any, current: SupplierSummary | null): SupplierSummary | null =>
  normaliseSupplier(raw ? { ...raw, plan_state: raw.plan_state ?? current?.plan_state } : raw);

/** The caller's supplier row, including Company Profile approval state. Loaded
 *  at app start so menus and routes can be gated before any screen fetches. */
export const loadMySupplier = apiThunk<any, void>("supplier/loadMe", {
  server: "login",
  url: "suppliers/me",
  method: "GET",
});

/** Submit the Company Profile for admin review, with the signed declaration.
 *  Locks the answers. */
export const submitMyProfile = apiThunk<any, Declaration>("supplier/submitProfile", {
  server: "login",
  url: "suppliers/me/profile/submit",
  method: "POST",
  body: (declaration) => ({ ...declaration }),
  successMsg: true,
  errorMsg: true,
});

/** Save the registered company details (registration/VAT numbers must be
 *  unique across suppliers — the API refuses a clash). */
export const updateMySupplier = apiThunk<any, Partial<RegisteredDetails>>("supplier/updateMe", {
  server: "login",
  url: "suppliers/me",
  method: "PUT",
  successMsg: true,
  errorMsg: true,
});

/** Every defined section with its server-computed status — all 11 from the
 *  first visit, unsaved ones as `not_started`. */
export const loadMySections = apiThunk<any, void>("supplier/loadMySections", {
  server: "login",
  url: "suppliers/me/sections",
  method: "GET",
  errorMsg: true,
});

/** Save one section. The server strips unknown fields and recomputes `status`,
 *  so the response is authoritative over whatever the form thought it had. */
export const saveMySection = apiThunk<any, { sectionKey: string; data: Record<string, any> }>(
  "supplier/saveMySection",
  {
    server: "login",
    url: ({ sectionKey }) => `suppliers/me/sections/${sectionKey}`,
    method: "PUT",
    body: ({ data }) => ({ data }),
    successMsg: true,
    errorMsg: true,
  }
);

/**
 * Upload one document against a question.
 *
 * Sent as FormData so Http.ts takes its multipart path — it omits Content-Type
 * for these, letting the browser set its own boundary, and skips encryption
 * because the payload is a file rather than a JSON envelope.
 */
export const uploadDocument = apiThunk<
  any,
  { sectionKey: string; fieldName: string; file: File }
>("supplier/uploadDocument", {
  server: "login",
  url: ({ sectionKey }) => `suppliers/me/sections/${sectionKey}/documents`,
  method: "POST",
  body: ({ fieldName, file }) => {
    const form = new FormData();
    form.append("field_name", fieldName);
    form.append("document", file);
    // apiThunk types bodies as a plain object; FormData is what Http.ts checks
    // for, so the cast is only to satisfy the signature.
    return form as unknown as Record<string, any>;
  },
  successMsg: true,
  errorMsg: true,
});

/** The supplier's own fleet — vehicles owned by this supplier, whether or not a
 *  driver is currently assigned. Same vehicle shape the web app renders. */
export const loadMyFleet = apiThunk<any, void>("supplier/loadMyFleet", {
  server: "login",
  url: "suppliers/me/fleet",
  method: "GET",
  errorMsg: true,
});

/** One vehicle, for the edit form. Ownership-checked server-side. */
export const loadFleetVehicle = apiThunk<any, { vehicleId: number | string }>(
  "supplier/loadFleetVehicle",
  {
    server: "login",
    url: ({ vehicleId }) => `customer-driver/driver-vehicle/${vehicleId}`,
    method: "GET",
    errorMsg: true,
  }
);

/** Create a vehicle. The API stamps supplier_id from the caller, so the client
 *  never asserts ownership itself. */
export const createFleetVehicle = apiThunk<any, Record<string, any>>(
  "supplier/createFleetVehicle",
  {
    server: "login",
    url: "customer-driver/driver-vehicle",
    method: "POST",
    successMsg: true,
    errorMsg: true,
  }
);

export const updateFleetVehicle = apiThunk<
  any,
  { vehicleId: number | string; data: Record<string, any> }
>("supplier/updateFleetVehicle", {
  server: "login",
  url: ({ vehicleId }) => `customer-driver/driver-vehicle/${vehicleId}`,
  method: "PUT",
  body: ({ data }) => data,
  successMsg: true,
  errorMsg: true,
});

/**
 * DVLA vehicle enquiry by registration — the same lookup the web app's Driver
 * Management uses (POST drivers/vehicle/check → DVLA vehicle-enquiry API).
 * The API answers 200 with { status: 'error' } for an unknown registration, so
 * callers check `status` rather than relying on rejection.
 */
export const checkVehicleRegistration = apiThunk<any, { vehicleNumber: string }>(
  "supplier/checkVehicleRegistration",
  {
    server: "login",
    url: "drivers/vehicle/check",
    method: "POST",
    body: ({ vehicleNumber }) => ({ vehicle_number: vehicleNumber }),
  }
);

/** Submit a vehicle's documents for admin approval — same call as the web
 *  app's vehicle requirements screen. The API needs can_submit_review =
 *  'validate' (every required document filled). */
export const submitVehicleKyc = apiThunk<any, { vehicleId: number | string }>(
  "supplier/submitVehicleKyc",
  {
    server: "login",
    url: ({ vehicleId }) => `customer-driver/vehicle-kyc/submit/${vehicleId}`,
    method: "GET",
    successMsg: true,
    errorMsg: true,
  }
);

/** Soft-delete. The API also expires any active driver assignment on the
 *  vehicle, which is why the UI warns before calling this. */
export const deleteFleetVehicle = apiThunk<any, { vehicleId: number | string }>(
  "supplier/deleteFleetVehicle",
  {
    server: "login",
    url: ({ vehicleId }) => `customer-driver/driver-vehicle/${vehicleId}`,
    method: "DELETE",
    successMsg: true,
    errorMsg: true,
  }
);

/** Take whichever driver is on this vehicle off it (they go offline). */
export const releaseFleetVehicle = apiThunk<any, { vehicleId: number | string }>(
  "supplier/releaseFleetVehicle",
  {
    server: "login",
    url: ({ vehicleId }) => `suppliers/me/fleet/${vehicleId}/driver`,
    method: "DELETE",
    successMsg: true,
    errorMsg: true,
  }
);

export const deleteDocument = apiThunk<any, { documentId: number }>(
  "supplier/deleteDocument",
  {
    server: "login",
    url: ({ documentId }) => `suppliers/me/documents/${documentId}`,
    method: "DELETE",
    successMsg: true,
    errorMsg: true,
  }
);

export interface FleetVehicle {
  id: number;
  supplier_id: number | null;
  vehicle_brand_name: string | null;
  vehicle_model_no: string | null;
  vehicle_registration: string | null;
  car_type_id: number | null;
  status: string | null;
  can_submit_review: string | null;
  vehicle_license_status: string | null;
  hackney_status: string | null;
  taxi_compliance_status: string | null;
  vehicle_insurance_status: string | null;
  vehicle_license_expired_date: string | null;
  hackney_expired_date: string | null;
  taxi_compliance_expired_date: string | null;
  vehicle_insurance_expired_date: string | null;
  /** The driver currently on this vehicle — at most one. */
  assigned_driver: {
    id: string;
    name: string;
    country_code: string;
    cust_mobile: string;
    login_status: string | null;
  } | null;
}

interface SupplierState {
  supplier: SupplierSummary | null;
  /** True once suppliers/me has answered (success or failure), so gates don't
   *  flash a "locked" screen before the status is known. */
  supplierLoaded: boolean;
  submitting: boolean;
  sections: SectionRow[];
  fleet: FleetVehicle[];
  fleetLoading: boolean;
  /** The vehicle being edited, hydrated from the API. */
  fleetVehicle: FleetVehicle | null;
  fleetSaving: boolean;
  /** Set once a create/update lands, so the form can navigate back. */
  fleetSaved: boolean;
  loading: boolean;
  saving: boolean;
  uploading: boolean;
  /** Set once a save lands, so the form screen can navigate back. */
  saved: boolean;
  errors: any;
}

const initialState: SupplierState = {
  supplier: null,
  supplierLoaded: false,
  submitting: false,
  sections: [],
  fleet: [],
  fleetLoading: false,
  fleetVehicle: null,
  fleetSaving: false,
  fleetSaved: false,
  loading: false,
  saving: false,
  uploading: false,
  saved: false,
  errors: null,
};

const supplierSlice = createSlice({
  name: "supplier",
  initialState,
  reducers: {
    clearSaved: (state) => {
      state.saved = false;
    },
    clearFleetSaved: (state) => {
      state.fleetSaved = false;
    },
    clearFleetVehicle: (state) => {
      state.fleetVehicle = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMySupplier.fulfilled, (state, action) => {
        state.supplier = normaliseSupplier(action.payload?.data);
        state.supplierLoaded = true;
      })
      .addCase(loadMySupplier.rejected, (state) => {
        state.supplierLoaded = true;
      })

      .addCase(updateMySupplier.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateMySupplier.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateMySupplier.rejected, (state, action) => {
        state.saving = false;
        state.errors = action.payload;
      })

      .addCase(submitMyProfile.pending, (state) => {
        state.submitting = true;
      })
      .addCase(submitMyProfile.fulfilled, (state, action) => {
        state.submitting = false;
        if (action.payload?.data) {
          state.supplier = normaliseSupplier({ ...(state.supplier || {}), ...action.payload.data });
        }
      })
      .addCase(submitMyProfile.rejected, (state, action) => {
        state.submitting = false;
        state.errors = action.payload;
      })

      .addCase(loadMySections.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(loadMySections.fulfilled, (state, action) => {
        state.loading = false;
        state.supplier = withPlanState(action.payload?.data?.supplier, state.supplier);
        state.supplierLoaded = true;
        state.sections = action.payload?.data?.sections ?? [];
      })
      .addCase(loadMySections.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload;
      })

      .addCase(saveMySection.pending, (state) => {
        state.errors = null;
        state.saving = true;
        state.saved = false;
      })
      .addCase(saveMySection.fulfilled, (state, action) => {
        state.saving = false;
        state.saved = true;

        // Patch the saved row in place from the server's response rather than
        // refetching: the API returns the cleaned data and computed status, so
        // the grid stays correct without a second round trip.
        const row = action.payload?.data;
        if (!row?.section_key) return;
        const index = state.sections.findIndex((s) => s.section_key === row.section_key);
        const existing = index >= 0 ? state.sections[index] : null;
        const next: SectionRow = {
          section_key: row.section_key,
          status: row.status,
          data: row.data ?? null,
          // Documents are untouched by a section save — keep what we already hold.
          documents: existing?.documents ?? [],
          updated_at: new Date().toISOString(),
        };
        if (index >= 0) state.sections[index] = next;
        else state.sections.push(next);
      })
      .addCase(saveMySection.rejected, (state, action) => {
        state.saving = false;
        state.errors = action.payload;
      })

      .addCase(uploadDocument.pending, (state) => {
        state.errors = null;
        state.uploading = true;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploading = false;
        const doc: SectionDocument | undefined = action.payload?.data;
        if (!doc?.section_key) return;
        const index = state.sections.findIndex((s) => s.section_key === doc.section_key);
        if (index < 0) return;
        state.sections[index].documents.push(doc);
        // An upload can be what takes a section to complete, and the server has
        // already recomputed that — but it doesn't return the new status here,
        // so the grid is refreshed on next load rather than guessed at.
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploading = false;
        state.errors = action.payload;
      })

      .addCase(loadMyFleet.pending, (state) => {
        state.errors = null;
        state.fleetLoading = true;
      })
      .addCase(loadMyFleet.fulfilled, (state, action) => {
        state.fleetLoading = false;
        state.fleet = action.payload?.data?.vehicles ?? [];
        // The fleet response carries the supplier summary too, so a direct hit
        // on the fleet screen populates the header without a second call.
        if (action.payload?.data?.supplier) state.supplier = withPlanState(action.payload.data.supplier, state.supplier);
      })
      .addCase(loadMyFleet.rejected, (state, action) => {
        state.fleetLoading = false;
        state.errors = action.payload;
      })

      // The vehicle endpoints answer { status, msg } with the row on `msg`.
      .addCase(loadFleetVehicle.fulfilled, (state, action) => {
        state.fleetVehicle = action.payload?.msg ?? null;
      })

      .addCase(createFleetVehicle.pending, (state) => {
        state.errors = null;
        state.fleetSaving = true;
        state.fleetSaved = false;
      })
      .addCase(createFleetVehicle.fulfilled, (state) => {
        state.fleetSaving = false;
        state.fleetSaved = true;
      })
      .addCase(createFleetVehicle.rejected, (state, action) => {
        state.fleetSaving = false;
        state.errors = action.payload;
      })

      .addCase(updateFleetVehicle.pending, (state) => {
        state.errors = null;
        state.fleetSaving = true;
        state.fleetSaved = false;
      })
      .addCase(updateFleetVehicle.fulfilled, (state) => {
        state.fleetSaving = false;
        state.fleetSaved = true;
      })
      .addCase(updateFleetVehicle.rejected, (state, action) => {
        state.fleetSaving = false;
        state.errors = action.payload;
      })

      .addCase(deleteFleetVehicle.fulfilled, (state, action) => {
        const id = action.meta.arg?.vehicleId;
        state.fleet = state.fleet.filter((v) => String(v.id) !== String(id));
      })
      .addCase(deleteFleetVehicle.rejected, (state, action) => {
        state.errors = action.payload;
      })

      .addCase(releaseFleetVehicle.fulfilled, (state, action) => {
        const id = action.meta.arg?.vehicleId;
        const vehicle = state.fleet.find((v) => String(v.id) === String(id));
        if (vehicle) vehicle.assigned_driver = null;
      })

      .addCase(deleteDocument.fulfilled, (state, action) => {
        const removed = action.payload?.data;
        const id = removed?.id ?? action.meta.arg?.documentId;
        if (!id) return;
        state.sections.forEach((section) => {
          section.documents = section.documents.filter((d) => String(d.id) !== String(id));
        });
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.errors = action.payload;
      });
  },
});

export const { clearSaved, clearFleetSaved, clearFleetVehicle } = supplierSlice.actions;
export default supplierSlice.reducer;
